<template>
  <va-modal
    v-model="state.isOpen"
    hide-default-actions
    :title="$t('users.editNameModal.title')"
  >
    <slot>
      <div class="bodyContainer">
        <va-input
          v-model="state.displayName"
          class="my-2"
          :label="$t('users.displayName')"
          :placeholder="$t('users.displayName')"
        />
        <va-input
          v-model="state.givenName"
          class="my-2"
          :label="$t('users.firstName')"
          :placeholder="$t('users.firstName')"
        />
        <va-input
          v-model="state.familyName"
          class="my-2"
          :label="$t('users.lastName')"
          :placeholder="$t('users.lastName')"
        />
      </div>
    </slot>
    <template #footer>
      <va-button flat outline class="mr-1" @click="emit('cancel')" :disabled="state.isUpdating">
        {{ $t("buttons.cancel") }}
      </va-button>
      <va-button
        class="ml-1"
        :disabled="!isFormValid || state.isUpdating"
        @click="updateUserName"
      >
        {{ $t("buttons.validate") }}
      </va-button>
    </template>
  </va-modal>
</template>

<script setup>
import { computed, inject, reactive, watch } from "vue";
import { useI18n } from "vue-i18n";
import { callUpdateUser } from "@/api/identity";

const { t } = useI18n();
const handleError = inject("handleError");
const toasts = inject("toasts");

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  },
  userId: {
    type: String,
    required: true
  },
  initialDisplayName: {
    type: String,
    required: true
  },
  initialGivenName: {
    type: String,
    required: true
  },
  initialFamilyName: {
    type: String,
    required: true
  }
});

const state = reactive({
  isOpen: computed(() => props.isOpen),
  displayName: props.initialDisplayName,
  givenName: props.initialGivenName,
  familyName: props.initialFamilyName,
  isUpdating: false
});

const isFormValid = computed(() => {
  return state.displayName?.trim() || state.givenName?.trim() || state.familyName?.trim();
});

// Reset form when modal opens
watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      state.displayName = props.initialDisplayName;
      state.givenName = props.initialGivenName;
      state.familyName = props.initialFamilyName;
      state.isUpdating = false;
    }
  }
);

const emit = defineEmits(["cancel", "validate"]);

const updateUserName = async () => {
  if (!isFormValid.value) {return;}

  state.isUpdating = true;
  try {
    const userData = {
      displayName: state.displayName?.trim() || null,
      givenName: state.givenName?.trim() || null,
      familyName: state.familyName?.trim() || null
    };

    await callUpdateUser(props.userId, userData);
    toasts.info(t("users.nameUpdated"));
    emit("validate");
  } catch (error) {
    handleError(error?.response?.data || error);
  } finally {
    state.isUpdating = false;
  }
};
</script>

<style lang="scss" scoped>
.bodyContainer {
  display: flex;
  flex-direction: column;
  min-width: 520px;
  padding: 1rem;
}
</style> 